import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function OrganizationRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organization_type: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    contact_person_name: '',
    contact_person_phone: '',
    contact_person_email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = { 
        ...formData,
        verify_password: formData.confirmPassword
      };
      delete payload.confirmPassword;
      
      await axios.post(`${API}/organization/register`, payload);
      toast.success('Registration successful! You can now login.');
      navigate('/organization/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </Link>
            <Link to="/organization/login">
              <Button variant="outline" className="font-bold uppercase text-sm">
                Organization Login
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Organization Registration</h1>
            <p className="text-muted-foreground">Register your organization to manage your vehicle fleet</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-accent p-4 rounded-sm">
              <h2 className="font-bold text-foreground mb-3">Organization Information</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Organization Name *</Label>
                  <Input
                    required
                    data-testid="org-name"
                    className="input-field mt-2"
                    placeholder="Your Organization Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Organization Type *</Label>
                  <Select value={formData.organization_type} onValueChange={(v) => setFormData({ ...formData, organization_type: v })}>
                    <SelectTrigger className="input-field mt-2" data-testid="org-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="government">Government Agency</SelectItem>
                      <SelectItem value="private">Private Company</SelectItem>
                      <SelectItem value="ngo">NGO / Non-Profit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Email *</Label>
                    <Input
                      type="email"
                      required
                      data-testid="org-email"
                      className="input-field mt-2"
                      placeholder="org@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Phone *</Label>
                    <Input
                      type="tel"
                      required
                      data-testid="org-phone"
                      className="input-field mt-2"
                      placeholder="+234 XXX XXX XXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Address *</Label>
                  <Textarea
                    required
                    data-testid="org-address"
                    className="input-field mt-2"
                    placeholder="Full organization address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm">
              <h2 className="font-bold text-blue-800 mb-3">Primary Contact Person</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide text-blue-700">Contact Name *</Label>
                  <Input
                    required
                    data-testid="contact-name"
                    className="input-field mt-2"
                    placeholder="Fleet Manager / Admin Name"
                    value={formData.contact_person_name}
                    onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide text-blue-700">Contact Email *</Label>
                    <Input
                      type="email"
                      required
                      data-testid="contact-email"
                      className="input-field mt-2"
                      placeholder="contact@company.com"
                      value={formData.contact_person_email}
                      onChange={(e) => setFormData({ ...formData, contact_person_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide text-blue-700">Contact Phone *</Label>
                    <Input
                      type="tel"
                      required
                      data-testid="contact-phone"
                      className="input-field mt-2"
                      placeholder="+234 XXX XXX XXXX"
                      value={formData.contact_person_phone}
                      onChange={(e) => setFormData({ ...formData, contact_person_phone: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
              <h2 className="font-bold text-green-800 mb-3">Login Credentials</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide text-green-700">Password *</Label>
                  <Input
                    type="password"
                    required
                    data-testid="org-password"
                    className="input-field mt-2"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>

                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide text-green-700">Confirm Password *</Label>
                  <Input
                    type="password"
                    required
                    data-testid="org-confirm-password"
                    className="input-field mt-2"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.organization_type}
              data-testid="org-register-btn"
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Registering...' : 'Register Organization'}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already registered?{' '}
            <Link to="/organization/login" className="text-primary font-bold hover:underline">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
